import { isNil } from 'lodash-es'
import {
  EntityStatus,
  ExtendedTradingEntityType,
  Organisation,
  SpreadsheetMapping,
  TradingEntity,
  TradingEntityType,
  ValidationIssue
} from 'api'
import { Locale, Translation } from 'locale'
import { gatherIssues, parseEnum, parseString, SpreadsheetParseResult } from '../upload/model'
import { Result } from 'utils'
import { createInvalidValueValidationIssue, Severity } from 'model'

import * as constants from 'view/styles/constants'

interface TradingEntitySpreadsheetParseRowResult {
  tradingEntity: TradingEntity
  orgShortCode: string | null
  parseIssues: ValidationIssue[]
}

export interface TradingEntitySpreadsheetParseResult extends SpreadsheetParseResult {
  tradingEntities: TradingEntity[]
}

export function parseTradingEntitySpreadsheet(
  filename: string,
  rows: any[],
  spreadsheetMapping: SpreadsheetMapping,
  getOrganisationByShortCode: (shortCode: string) => Organisation | null,
  countryCodes?: string[]
): TradingEntitySpreadsheetParseResult {
  const combinations: string[] = []
  const duplicateCombinations: string[] = []
  return rows.reduce<TradingEntitySpreadsheetParseResult>(
    (spreadsheetResult, row, rowNumber) => {
      const rowResult = parseRow(row, rowNumber + 1, spreadsheetMapping, getOrganisationByShortCode, countryCodes)

      const organisationAndShortNameCombo = `${rowResult.orgShortCode}-${rowResult.tradingEntity.shortName}`
      const shortNameAndLeiCombo = `${rowResult.tradingEntity.shortName}-${rowResult.tradingEntity.lei}`

      if (combinations.includes(organisationAndShortNameCombo)) {
        duplicateCombinations.push(organisationAndShortNameCombo)
        spreadsheetResult.issues.push(
          createInvalidValueValidationIssue(
            'Organisation and Short Name',
            `Duplicate combination of organisation "${rowResult.orgShortCode}" and short name "${rowResult.tradingEntity.shortName}"`,
            Severity.ERROR
          )
        )
      } else if (combinations.includes(shortNameAndLeiCombo)) {
        duplicateCombinations.push(shortNameAndLeiCombo)
        spreadsheetResult.issues.push(
          createInvalidValueValidationIssue(
            'Short Name and LEI',
            `Duplicate combination of short name "${rowResult.tradingEntity.shortName}" and LEI "${rowResult.tradingEntity.lei}"`
          )
        )
      } else {
        combinations.push(organisationAndShortNameCombo)
        combinations.push(shortNameAndLeiCombo)
      }

      spreadsheetResult.issues.push(...rowResult.parseIssues)
      if (
        rowResult.tradingEntity &&
        rowResult.parseIssues.length === 0 &&
        !duplicateCombinations.includes(organisationAndShortNameCombo) &&
        !duplicateCombinations.includes(shortNameAndLeiCombo)
      ) {
        spreadsheetResult.tradingEntities.push(rowResult.tradingEntity)
      }

      return spreadsheetResult
    },
    { filename, rows, issues: [], tradingEntities: [] }
  )
}

function validGfidCode(organisationGfids: string[] | undefined, gfidCode: string): boolean {
  if (!organisationGfids || !gfidCode) return false

  const normalizedGfidCode = gfidCode.trim().toLowerCase()

  return organisationGfids.some(gfid => gfid.trim().toLowerCase() === normalizedGfidCode)
}

function parseRow(
  row: any,
  rowNum: number,
  spreadsheetMapping: SpreadsheetMapping,
  getOrganisationByShortCode: (shortCode: string) => Organisation | null,
  countryCodes?: string[]
): TradingEntitySpreadsheetParseRowResult {
  const organisationResult = parseString(row, rowNum, spreadsheetMapping.organisation, Locale.translate(Translation.TC_ORGANISATION), true)
  const publishedNameResult = parseString(
    row,
    rowNum,
    spreadsheetMapping.publishedName,
    Locale.translate(Translation.TC_PUBLISHED_NAME),
    true
  )
  const shortNameResult = parseString(row, rowNum, spreadsheetMapping.shortName, Locale.translate(Translation.TC_SHORT_NAME), true)
  const ficcShortNameResult = parseString(
    row,
    rowNum,
    spreadsheetMapping.ficcShortName,
    Locale.translate(Translation.SC_FICC_SHORT_NAME),
    false
  )
  const statusResult = parseEnum<EntityStatus>(
    row,
    rowNum,
    spreadsheetMapping.tradingStatus,
    spreadsheetMapping.tradingStatus.values,
    Locale.translate(Translation.TC_STATUS),
    false,
    true
  )
  const leiResult = parseString(row, rowNum, spreadsheetMapping.lei, Locale.translate(Translation.TC_LEI), true)
  const bicResult = parseString(row, rowNum, spreadsheetMapping.bic, Locale.translate(Translation.TC_BIC), true)
  const tradingEntityTypeResult = parseEnum<ExtendedTradingEntityType>(
    row,
    rowNum,
    spreadsheetMapping.tradingEntityType,
    spreadsheetMapping.tradingEntityType.values,
    Locale.translate(Translation.TC_TRADING_TYPE),
    false,
    true
  )

  const euGfidCodeResult = parseString(row, rowNum, spreadsheetMapping.euGfidCode, Locale.translate(Translation.TC_EU_GFID), false)
  const usGfidCodepResult = parseString(row, rowNum, spreadsheetMapping.usGfidCode, Locale.translate(Translation.TC_US_GFID), false)
  const eligible15a6Result = parseString(
    row,
    rowNum,
    spreadsheetMapping.eligible15a6,
    Locale.translate(Translation.TC_15A6_ELIGIBLE),
    false
  )
  const eligibleCombinedRecFilepResult = parseString(
    row,
    rowNum,
    spreadsheetMapping.eligibleCombinedRecFile,
    Locale.translate(Translation.TC_COMBINED_EU_US_EOD_FILE),
    false
  )
  const centralBankResultResult = parseString(
    row,
    rowNum,
    spreadsheetMapping.centralBank,
    Locale.translate(Translation.TC_CENTRAL_BANK),
    false
  )
  const negotiationHistoryFileResult = parseString(
    row,
    rowNum,
    spreadsheetMapping.negotiationHistoryFile,
    Locale.translate(Translation.TC_NEGOTIATION_HISTORY_FILE),
    false
  )

  const operatingCountryResult = parseString(
    row,
    rowNum,
    spreadsheetMapping.operatingCountry,
    Locale.translate(Translation.TC_OPERATING_COUNTRY),
    !centralBankResultResult.isNull
  )

  const parseIssues = gatherIssues(
    organisationResult,
    publishedNameResult,
    shortNameResult,
    ficcShortNameResult,
    statusResult,
    leiResult,
    bicResult,
    tradingEntityTypeResult,
    euGfidCodeResult,
    usGfidCodepResult,
    eligible15a6Result,
    eligibleCombinedRecFilepResult,
    centralBankResultResult,
    negotiationHistoryFileResult,
    operatingCountryResult
  )

  const organisationId = getOrganisationId(organisationResult, getOrganisationByShortCode)

  if (isNil(organisationId) && organisationResult.isValue) {
    parseIssues.push(
      createInvalidValueValidationIssue(
        Locale.translate(Translation.TC_ORGANISATION),
        `Cannot find organisation with name "${organisationResult.value}"`,
        Severity.ERROR
      )
    )
  }

  if (!isNil(operatingCountryResult) && operatingCountryResult.isValue && !countryCodes?.includes(operatingCountryResult.value)) {
    parseIssues.push(
      createInvalidValueValidationIssue(
        Locale.translate(Translation.TC_OPERATING_COUNTRY),
        `Cannot find ISO country code with name "${operatingCountryResult.value}"`,
        Severity.ERROR
      )
    )
  }

  const organisationGfids = getOrganisationByShortCode(Result.getValue(organisationResult) || '')?.gfidCodes

  if (!isNil(euGfidCodeResult) && euGfidCodeResult.isValue && !validGfidCode(organisationGfids, euGfidCodeResult.value)) {
    parseIssues.push(
      createInvalidValueValidationIssue(
        Locale.translate(Translation.TC_EU_GFID),
        `Cannot find EU GFID with code "${euGfidCodeResult.value}"`,
        Severity.ERROR
      )
    )
  }

  if (!isNil(usGfidCodepResult) && usGfidCodepResult.isValue && !validGfidCode(organisationGfids, usGfidCodepResult.value)) {
    parseIssues.push(
      createInvalidValueValidationIssue(
        Locale.translate(Translation.TC_US_GFID),
        `Cannot find US GFID with code "${usGfidCodepResult.value}"`,
        Severity.ERROR
      )
    )
  }

  const tradingEntityTypes = getTradingEntityTypes(tradingEntityTypeResult)

  return {
    parseIssues,
    tradingEntity: {
      id: null!,
      organisationId: organisationId!,
      publishedName: publishedNameResult.isValue ? publishedNameResult.value : null!,
      shortName: shortNameResult.isValue ? shortNameResult.value.slice(0, constants.tradingEntityIdMaxFieldLength) : null!,
      status: statusResult.isValue ? statusResult.value : EntityStatus.DISABLED,
      lei: leiResult.isValue ? leiResult.value : null!,
      bic: bicResult.isValue ? bicResult.value : null!,
      tradingEntityTypes,
      group: '',
      settlementVenue: [],
      productRights: [],
      splitUsTrades: false,
      eligible15a6: Result.getValue(eligible15a6Result) === 'Y' ? true : false,
      eligibleCombinedRecFile: Result.getValue(eligibleCombinedRecFilepResult) === 'Y' ? true : false,
      centralBank: Result.getValue(centralBankResultResult) === 'Y' ? true : false,
      negotiationHistoryFile: Result.getValue(negotiationHistoryFileResult) === 'Y' ? true : false,
      euGfidCode: Result.getValue(euGfidCodeResult),
      usGfidCode: Result.getValue(usGfidCodepResult),
      operatingCountry: Result.getValue(operatingCountryResult),
      roundPriceToOneEighth: false,
      ficcShortName: ficcShortNameResult.isValue ? ficcShortNameResult.value.slice(0, constants.tradingEntityIdMaxFieldLength) : null!
    },
    orgShortCode: organisationResult.isValue ? organisationResult.value : null
  }
}

function getOrganisationId(
  organisationResult: Result<string>,
  getOrganisationByShortCode: (shortCode: string) => Organisation | null
): string | null {
  if (!organisationResult.isValue) {
    return null
  }

  const organisation = getOrganisationByShortCode(organisationResult.value)
  return organisation ? organisation.id : null
}

function getTradingEntityTypes(tradingEntityTypeResult: Result<ExtendedTradingEntityType>): TradingEntityType[] {
  if (!tradingEntityTypeResult.isValue) {
    return null!
  }

  switch (tradingEntityTypeResult.value) {
    case ExtendedTradingEntityType.STANDARD:
      return [TradingEntityType.STANDARD]
    case ExtendedTradingEntityType.BLOCK:
      return [TradingEntityType.BLOCK]
    case ExtendedTradingEntityType.BOTH:
      return [TradingEntityType.STANDARD, TradingEntityType.BLOCK]
    default:
      throw new Error(`Unhandled trading entity type ${[tradingEntityTypeResult.value]}`)
  }
}
